import i18n from "i18next";

import type { AppLocale } from "@/lib/locale/resolve-effective-locale";
import type { LocalizedString, MaritimeWarning } from "@/lib/api";
import {
  normalizeWarningLevel,
  WarningLevel,
  warningLevelI18nKey,
} from "@/lib/warning-level";
import type { MaritimeWarningDomainId } from "@/lib/maritime-domains";
import { maritimeToolbarLevel } from "@/lib/warning-page-copy";

import type { MaritimeArea } from "./maritime-areas.queries";

export function highestMaritimeWarningLevelForEnabledDomains(
  warnings: MaritimeWarning[],
  areas: MaritimeArea[],
  enabledDomainIds: MaritimeWarningDomainId[],
): WarningLevel {
  if (enabledDomainIds.length === 0) {
    return WarningLevel.Green;
  }

  const enabledAreaIds = new Set(
    areas.filter((area) => enabledDomainIds.includes(area.domainId)).map((area) => area.id),
  );

  if (enabledAreaIds.size === 0) {
    return WarningLevel.Green;
  }

  return warnings.reduce<WarningLevel>((worst, warning) => {
    if (!enabledAreaIds.has(warning.area_id)) {
      return worst;
    }
    return Math.max(worst, normalizeWarningLevel(warning.level)) as WarningLevel;
  }, WarningLevel.Green);
}

export function getLocalizedString(
  text: LocalizedString | undefined,
  locale: AppLocale,
): string {
  if (!text) return "";
  return text[locale] ?? text.nl ?? text.en ?? "";
}

export function getAreaName(area: MaritimeArea, locale: AppLocale): string {
  return getLocalizedString(area.name, locale) || area.id;
}

export function getWarningDescription(warning: MaritimeWarning, locale: AppLocale): string {
  return getLocalizedString(warning.description, locale);
}

/**
 * Maritime levels are shown by color name only, never "code …" (PO). Lowercase variant for
 * the badges in the category picker and side panel: "groen", "geel", …
 */
export function formatWarningLevelBadge(level: WarningLevel, locale: AppLocale): string {
  return i18n.t(warningLevelI18nKey[level], { lng: locale });
}

/** Sentence-case variant for the legend: "Groen", "Geel", … */
export function formatWarningLevelLegend(level: WarningLevel, locale: AppLocale): string {
  return maritimeToolbarLevel(formatWarningLevelBadge(level, locale));
}
